import type { FilesApi } from '../files/FilesApi'
import type { UgcApi, UgcEntry } from '../ugc/UgcApi'
import {
  CLIP_CONTENT_TYPE,
  type CaptureConsent,
  type ClipBlob,
  type ClipPersistOptions,
  type ClipResult,
  type ClipsApi,
  type ClipsSupport,
  type StartClipRecordingOptions,
} from './ClipsApi'
import type { CaptureConsentPort } from './CaptureConsentPort'
import { ClipRecorder } from './ClipRecorder'
import {
  isCameraSupported,
  isMicrophoneSupported,
  isRecordingSupported,
  normalizeAudio,
  normalizeCamera,
  queryDevicePermission,
} from './clipRecorderUtils'

interface ClipsDeps {
  files: FilesApi
  ugc: UgcApi
  captureConsent: CaptureConsentPort
}

interface RecorderLike {
  isRecording(): boolean
  useGameAudio(node: AudioNode): void
  start(options?: StartClipRecordingOptions): Promise<void>
  captureFrame(): void
  stop(): Promise<ClipBlob>
  cancel(): void
}

function extensionForMime(mimeType: string): string {
  return mimeType.includes('mp4') ? 'mp4' : 'webm'
}

export class ClipsApiImpl implements ClipsApi {
  private readonly recorder: RecorderLike

  constructor(
    private readonly deps: ClipsDeps,
    recorderFactory: () => RecorderLike = () => new ClipRecorder(),
  ) {
    this.recorder = recorderFactory()
  }

  async isSupportedAsync(): Promise<ClipsSupport> {
    const canRecord = isRecordingSupported()
    return {
      canRecord,
      canUseMicrophone: canRecord && isMicrophoneSupported(),
      canUseCamera: canRecord && isCameraSupported(),
      reason: canRecord ? undefined : 'MediaRecorder/captureStream unavailable',
    }
  }

  getCaptureConsentAsync(): Promise<CaptureConsent> {
    return this.deps.captureConsent.get()
  }

  async requestCaptureConsentAsync(opts?: { includesCamera?: boolean }): Promise<CaptureConsent> {
    const existing = await this.deps.captureConsent.get()
    if (existing.status === 'granted') return existing
    if (existing.status === 'denied') return existing

    // If the browser/OS hasn't granted device permission yet, it will show its
    // own prompt during startRecordingAsync — skip the platform dialog to avoid
    // a double opt-in. Query the permission the recording will actually use.
    const permName = opts?.includesCamera ? 'camera' : 'microphone'
    const deviceState = await queryDevicePermission(permName)
    if (deviceState === 'prompt') return { status: 'granted', canAskAgain: true }

    return this.deps.captureConsent.request({ includesCamera: opts?.includesCamera })
  }

  useGameAudio(node: AudioNode): void {
    this.recorder.useGameAudio(node)
  }

  captureFrame(): void {
    this.recorder.captureFrame()
  }

  async cancelRecordingAsync(): Promise<void> {
    this.recorder.cancel()
  }

  async startRecordingAsync(options?: StartClipRecordingOptions): Promise<void> {
    const audio = normalizeAudio(options?.audio)
    const camera = normalizeCamera(options?.camera)
    if (audio.microphone || camera.enabled) {
      await this.ensureCaptureConsent({ includesCamera: camera.enabled })
    }
    await this.recorder.start(options)
  }

  /**
   * Throws CAPTURE_CONSENT_DENIED if consent is denied or the platform dialog
   * is declined. Otherwise resolves and recording may proceed.
   *
   * Logic:
   * - T1 / already granted (get() returns granted) → proceed
   * - Already denied → throw
   * - Device permission is 'prompt' → skip platform dialog; the browser/OS shows
   *   its own prompt during getUserMedia (single opt-in). The host cannot
   *   securely self-grant from a game signal, so app consent stays
   *   'undetermined' and the platform dialog shows on the next recording.
   * - Device permission is 'denied' → skip, let getUserMedia fail naturally
   * - Device permission is 'granted' → show the platform dialog (no system
   *   dialog is coming, so this is the user's single opt-in)
   */
  private async ensureCaptureConsent(opts?: { includesCamera?: boolean }): Promise<void> {
    const consent = await this.deps.captureConsent.get()
    if (consent.status === 'granted') return
    if (consent.status === 'denied') throw new Error('CAPTURE_CONSENT_DENIED')

    // consent is 'undetermined' — check device permission to decide the opt-in path.
    const permName = opts?.includesCamera ? 'camera' : 'microphone'
    const deviceState = await queryDevicePermission(permName)

    // Browser/OS will show its own prompt, or already denied — let getUserMedia drive it.
    if (deviceState === 'prompt' || deviceState === 'denied') return

    // Device permission is already granted (e.g. from another game on this origin).
    // No system dialog will appear, so we must show the platform consent dialog.
    const result = await this.deps.captureConsent.request({ includesCamera: opts?.includesCamera })
    if (result.status !== 'granted') {
      throw new Error('CAPTURE_CONSENT_DENIED')
    }
  }

  async stopRecordingAsync(persist?: ClipPersistOptions): Promise<ClipResult> {
    const clip = await this.recorder.stop()
    if ((persist?.persist ?? 'ugc') === 'none') return { ...clip }

    const quota = await this.deps.files.getQuota()
    if (clip.sizeBytes > quota.maxFileBytes) {
      throw new Error(
        `[clips] clip is ${clip.sizeBytes} bytes which exceeds the ` +
          `${quota.maxFileBytes}-byte upload cap; lower maxDurationMs, fps, or videoBitsPerSecond`,
      )
    }

    const key = `clips/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}.${extensionForMime(clip.mimeType)}`

    const upload = await this.deps.files.upload({
      key,
      contentType: clip.mimeType,
      sizeBytes: clip.sizeBytes,
      visibility: 'private',
    })

    let fileEntry: Awaited<ReturnType<FilesApi['confirmUpload']>>
    try {
      const putResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: upload.uploadHeaders,
        body: clip.blob,
      })
      if (!putResponse.ok) {
        throw new Error(
          `[clips] upload PUT failed with status ${putResponse.status}`,
        )
      }

      fileEntry = await this.deps.files.confirmUpload(upload.key)
    } catch (err) {
      await this.deps.files.delete(upload.key).catch(() => undefined)
      throw err
    }

    // fileKey is the canonical, durable reference — fileEntry.url is a ~4h signed
    // URL, so it is intentionally NOT persisted in UGC data. Consumers resolve a
    // fresh URL via files.getUrl({ key }).
    const data: Record<string, unknown> = {
      ...persist?.data,
      fileKey: upload.key,
      durationMs: clip.durationMs,
      width: clip.width,
      height: clip.height,
      mimeType: clip.mimeType,
      codec: fileEntry.mediaMetadata?.codec,
    }

    let entry: UgcEntry
    try {
      entry = await this.deps.ugc.create({
        contentType: CLIP_CONTENT_TYPE,
        data,
        isPublic: false,
        title: persist?.title,
        tags: persist?.tags,
      })
    } catch (err) {
      // Compensating cleanup: the file is confirmed/active but has no UGC entry.
      // Delete it so it does not orphan and consume creator quota.
      await this.deps.files.delete(upload.key).catch(() => undefined)
      throw err
    }

    return {
      ...clip,
      ugc: entry,
      fileKey: upload.key,
      fileUrl: fileEntry.url,
    }
  }

  async publishClipAsync(ugcId: string): Promise<UgcEntry> {
    const entry = await this.deps.ugc.get(ugcId)
    if (!entry) throw new Error(`[clips] UGC entry "${ugcId}" not found`)
    const fileKey = entry.data?.fileKey
    if (typeof fileKey !== 'string' || fileKey.length === 0) {
      // Fail loud: never flip a clip public without an accessible file behind it.
      throw new Error(
        `[clips] UGC entry "${ugcId}" has no data.fileKey; refusing to publish ` +
          'a clip with no accessible file',
      )
    }
    await this.deps.files.setVisibility(fileKey, 'public')
    try {
      return await this.deps.ugc.update({ id: ugcId, isPublic: true })
    } catch (err) {
      // Best-effort revert so we never leave a public file behind a private entry.
      await this.deps.files.setVisibility(fileKey, 'private').catch(() => undefined)
      throw err
    }
  }
}
